/**
 * AGROMX Frontend - Tractor Service
 * Servicio para gestión de tractores
 */

import api from './api.js';

class TractorService {
  /**
   * Listar tractores con paginación y filtros
   */
  async list(params = {}) {
    try {
      const response = await api.get('/tractores', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtener tractor por número de serie.
   * Usa el endpoint público /lookup/:numSerie (sin requirePermission)
   * para que módulos operativos puedan consultar tractores sin tener
   * el permiso `tractores:ver` del módulo Tractores.
   */
  async getByNumSerie(numSerie) {
    try {
      const response = await api.get(`/tractores/lookup/${numSerie}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtener datos básicos de un tractor por número de serie
   */
  async getTractorBasicoByNumSerie(numSerie) {
    try {
      const response = await api.get(`/tractores/${numSerie}/basico`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtener estado completo del tractor
   */
  async getEstado(numSerie) {
    try {
      const response = await api.get(`/tractores/${numSerie}/estado`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * La historia de la unidad: cada movimiento con los días que pasaron entre uno y otro.
   */
  async getHistoria(numSerie) {
    try {
      const response = await api.get(`/tractores/${numSerie}/historia`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Cuánto tarda cada tramo sobre todas las unidades: dónde se atoran.
   */
  async getTiemposPorTramo() {
    try {
      const response = await api.get('/tractores/tiempos-por-tramo');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtener estadísticas
   */
  async getStats() {
    try {
      const response = await api.get('/tractores/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Crear nuevo tractor
   */
  async create(data) {
    try {
      const response = await api.post('/tractores', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Actualizar tractor
   */
  async update(numSerie, data) {
    try {
      const response = await api.put(`/tractores/${numSerie}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Eliminar tractor
   */
  async delete(numSerie) {
    try {
      await api.delete(`/tractores/${numSerie}`);
      return true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reactivar tractor eliminado
   */
  async reactivar(numSerie) {
    try {
      const response = await api.put(`/tractores/${numSerie}/reactivar`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtener opciones de marcas
   */
  getMarcas() {
    return ['LOVOL', 'MAQMUT', 'AOSHENG', 'BEE MACH'];
  }

  /**
   * Obtener opciones de estados
   */
  getEstados() {
    return [
      { value: 'en_almacen', label: 'En Almacén' },
      { value: 'en_produccion', label: 'En Producción' },
      { value: 'liberado', label: 'Liberado' },
      { value: 'entregado', label: 'Entregado' },
      { value: 'en_servicio', label: 'En Servicio' }
    ];
  }

  /**
   * Formatear estado para display
   */
  formatEstado(estado) {
    const estados = {
      'en_almacen': 'En Almacén',
      'en_produccion': 'En Producción',
      'liberado': 'Liberado',
      'entregado': 'Entregado',
      'en_servicio': 'En Servicio',
      // ERP F1 · B3 (migración 09): estados nuevos de la unidad
      'por_llegar': 'Por llegar',
      'en_transito': 'En tránsito',
      'en_consigna': 'En consigna',
      'vendido': 'Vendido',
      'recuperado': 'Recuperado',
      'baja': 'Baja'
    };
    return estados[estado] || estado;
  }

  /**
   * Obtener badge class por estado
   */
  getEstadoBadgeClass(estado) {
    const classes = {
      'en_almacen': 'badge-info',
      'en_produccion': 'badge-warning',
      'liberado': 'badge-success',
      'entregado': 'badge-success',
      'en_servicio': 'badge-warning'
    };
    return classes[estado] || 'badge-info';
  }
}

export default new TractorService();
